import type { Operation } from 'apollo-link';
import type { RequestParameters } from 'relay-runtime';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';

// TODO: in the future, map all available query keys/responses programatically
export const QUERY_OVERRIDE_KEYS = {
	IS_NEW_USER: 'Query.isNewUser',
	IS_SITE_ADMIN: 'Query.isSiteAdmin',
	IS_ESTABLISHED_USER: 'ExperimentalUserPersonalization.isEstablishedUser',
	ORIGIN_PRODUCT: 'onboarding-origin-state:attributes',
};

/**
 * Loads query overrides from localStorage
 * @returns Record of override keys to values, or empty object if none found
 */
export const loadOverridesFromStorage = (): Record<string, any> => {
	if (!localStorage) {
		return {};
	}

	try {
		return JSON.parse(localStorage.getItem(keys.QUERY_OVERRIDES)) || {};
	} catch {
		return {};
	}
};

/**
 * Applies query overrides to response data recursively
 * @param response - The response object to modify
 * @param overrides - The overrides to apply
 */
const applyQueryOverrides = (
	response: Record<string, any>,
	overrides: Record<string, any>,
): void => {
	if (!response.data || typeof response.data !== 'object') {
		return;
	}

	function deepApplyQueryOverrides(data: typeof response, typename?: string) {
		if (data == null || typeof data !== 'object') return data;

		if (!typename) {
			typename = data.__typename;
			if (!typename) return data;
		}

		data = shallowApplyQueryOverrides(data, typename);

		Object.entries(data).forEach(([key, value]) => (data[key] = deepApplyQueryOverrides(value)));

		return data;
	}

	const overrideEntries = Object.entries(overrides);

	function shallowApplyQueryOverrides(data: typeof response, typename: string) {
		const typenamePrefix = `${typename}.`;

		overrideEntries.forEach(([overrideKey, overrideValue]) => {
			if (!overrideKey.startsWith(typenamePrefix)) return;

			const field = overrideKey.substring(typenamePrefix.length);

			if (!(field in data)) return;

			// Match the type of the override to the type of the field:
			switch (typeof data[field]) {
				case 'boolean':
					overrideValue = overrideValue === 'true';
					break;
				case 'number':
					overrideValue = Number(overrideValue);
					break;
			}

			data[field] = overrideValue;
		});

		return data;
	}

	deepApplyQueryOverrides(response.data, 'Query');
};

/**
 * Applies established user override to response data
 * @param data - The response data object to modify
 * @param overrides - The overrides to apply
 */
const applyEstablishedUserOverride = (
	data: Record<string, any>,
	overrides: Record<string, any>,
): void => {
	if (!data?.experimentalUserPersonalization) {
		return;
	}

	let isEstablishedUserTraitName = 'confluence_is_established_user';
	const { hostname } = window.location;
	if (hostname.endsWith('.jira-dev.com')) {
		isEstablishedUserTraitName = 'confluence_is_established_user_stg';
	}
	if (
		data.experimentalUserPersonalization.__typename === 'ExperimentalUserPersonalization' &&
		QUERY_OVERRIDE_KEYS.IS_ESTABLISHED_USER in overrides
	) {
		if (data.experimentalUserPersonalization.attributes) {
			const filteredAttributes = data.experimentalUserPersonalization.attributes.filter(
				(i: any) => i.name !== isEstablishedUserTraitName,
			);

			data.experimentalUserPersonalization.attributes = [
				...filteredAttributes,
				{
					name: isEstablishedUserTraitName,
					value: (overrides[QUERY_OVERRIDE_KEYS.IS_ESTABLISHED_USER] === 'true').toString(),
					__typename: 'ExperimentalUserPersonalizationAttributes',
				},
			];
		}
	}
};

/**
 * Applies onboarding state override to response data
 * @param data - The response data object to modify
 * @param overrides - The overrides to apply
 */
const applyOnboardingStateOverride = (
	data: Record<string, any>,
	overrides: Record<string, any>,
): void => {
	if (!data.onboardingState) {
		return;
	}

	data.onboardingState = data.onboardingState.map((state: any) => {
		const { __typename, key } = state;
		if (__typename === 'OnboardingState' && key in overrides) {
			return { ...state, value: overrides[key].toString() };
		}
		return state;
	});
};

/**
 * Checks if the operation is a query operation
 * @param apolloOperation - Apollo's Operation format
 * @param relayRequest - Relay's RequestParameters format
 * @returns true if the operation is a query
 */
const isQueryOperation = ({
	apolloOperation,
	relayRequest,
}: {
	apolloOperation?: Operation;
	relayRequest?: RequestParameters;
}): boolean => {
	// Handle Relay format (has operationKind directly)
	if (relayRequest?.operationKind) {
		return relayRequest.operationKind === 'query';
	}
	// Handle Apollo format (has query.definitions)
	if (apolloOperation?.query?.definitions) {
		return apolloOperation.query.definitions.some(
			(definition: any) => 'operation' in definition && definition.operation === 'query',
		);
	}
	return false;
};

/**
 * Applies all query overrides to a response
 * @param options - All options including overrides, response and operation info
 * @returns The modified response
 */
export const applyQueryOverridesToResponse = <T extends Record<string, any>>({
	overrides,
	response,
	apolloOperation,
	relayRequest,
}: {
	overrides: Record<string, any>;
	response: T;
	apolloOperation?: Operation;
	relayRequest?: RequestParameters;
}): T => {
	if (Object.keys(overrides).length === 0) {
		return response;
	}

	if (isQueryOperation({ apolloOperation, relayRequest })) {
		applyQueryOverrides(response, overrides);
	}

	if (response?.data != null && typeof response.data === 'object') {
		applyEstablishedUserOverride(response.data, overrides);
		applyOnboardingStateOverride(response.data, overrides);
	}

	return response;
};
